import { apiRoute } from '@/api/ApiRoute';
import { ACCESS_READ } from '@/constant/constant';
import { CommonInfo, MenuInfo, UserInfo } from '@/types';
import request from '@/utils/request';
import { actionName, setRequestInfo } from '@/utils/requestInfo';
import storage from '@/utils/storage';
import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';

//PageKeyとFunctionIdのマッピング
const pageKeyToFunctionId: Record<string, string> = {
  // マスター系
  // カレンダーマスタ管理画面							A01F01
  CALENDARCODE: 'A01F01',
  // 稼働カレンダーマスタ管理画面							A02F01
  WORKINGCALENDAR: 'A02F01',
  // PXP対応カレンダーマスタ管理画面							A03F01
  CORRESPONDENCECALENDAR: 'A03F01',
  // 発注イベント日マスタ管理画面							A04F01
  EVENTMASTER: 'A04F01',
  // 品番マスタ管理画面							B01F01
  PARTMASTER: 'B01F01',
  // ペア部品マスタ管理画面							B02F01
  PAIRINGREGISTRATION: 'B02F01',
  // 品番グループマスタ管理画面							B03F01
  GROUPINGREGISTRATION: 'B03F01',
  // 少量まとめ対象箱数マスタ管理画面							B04F01
  COLLECTIVEVANNING: 'B04F01',
  // 打ち切り品番マスタ管理画面							B05F01
  DISCONTINUEDPART: 'B05F01',
  // 品番別梱包条件メンテ画面							D01F01
  PACKINGCONDITIONMAINTENANCE: 'D01F01',
  //PxPオーダー
  // PXPオーダー変動チェック画面							I01F01
  ORDERVOLATILITYSEARCH: 'I01F01',
  // PXPオーダー管理画面							I02F01
  ORDERCOMPILING: 'I02F01',
  // PXP受注原数/計画数履歴画面							I03F01
  ORDERPLANHISTORY: 'I03F01',
  // BOM Check List表示画面							I04F01
  BOMCHECKLIST: 'I04F01',

  //梱包計画
  //梱包計画管理画面							J01F01
  PACKINGPLAN: 'J01F01',
  //梱包計画管理画面(月次)							J01F01
  PACKINGPLANMONTHLY: 'J01F01-1',
  //梱包計画管理画面(週次)							J01F02
  PACKINGPLANWEEKLY: 'J01F01-2',
  //梱包計画管理画面(日次)							J01F03
  PACKINGPLANDAILY: 'J01F01-3',
  // 帳票出力画面							K01F01
  REPORTOUTPUT: 'K01F01',
  //結果管理
  // 進度管理画面							L01F01
  PACKINGPROGRESS: 'L01F01',
  // 梱包実績管理_パーツ画面							L02F01
  PACKINGINFORMATION: 'L02F01',
  // 梱包実績管理_ケース画面							L03F01
  CASEINFORMATION: 'L03F01',
  // 梱包実績管理_ケース画面詳細							L03F02
  CASEINFORMATIONDETAIL: 'L03F02',
  // 注残管理画面							L04F01
  ORDEROCFASN: 'L04F01',
  // 梱包実績メンテ画面							D02F01
  PACKINGRESULTSMAINTENANCE: 'D02F01',
  //ジョブ管理		ジョブ管理画面							M01F01
  JOBNETKICK: 'M01F01',
  // TOPページ								C01F02
  TOPPAGE: 'C01F02',
};

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string>('');
  const userInfo = reactive<UserInfo>({
    employeeId: '',
    employeeName: '',
    prctCode: '',
    accessPrctCodeList: [],
    accessPrctCode: '',
    roleType: '',
    roleName: '',
    idToken: '',
  });
  const menuItems = ref<MenuInfo[]>([]);
  const commonInfo = reactive<CommonInfo>({
    maxUploadFileSize: 0,
  });
  // システム設定のページサイズ情報を保存
  const screenPageSizeInfoList = ref<any[]>([]);

  const restoreFromStorage = () => {
    try {
      // SessionStorageからユーザ情報とメニューアイテムを取得
      // 共通情報を設定
      const parsedUserInfo = JSON.parse(storage.getItem('pxp_userInfo'));
      Object.assign(userInfo, parsedUserInfo);
      menuItems.value = JSON.parse(storage.getItem('pxp_menuItems'));
      Object.assign(commonInfo, JSON.parse(storage.getItem('pxp_commonInfo')));
      screenPageSizeInfoList.value = JSON.parse(storage.getItem('pxp_screenPageSizeInfoList') || '[]');
      token.value = storage.getItem('pxp_authToken');
    } catch (error) {
      console.error('sessionStorageからの復元エラー:', error);
    }
  };

  const saveToStorage = () => {
    try {
      storage.setItem('pxp_userInfo', JSON.stringify(userInfo));
      storage.setItem('pxp_menuItems', JSON.stringify(menuItems.value));
      storage.setItem('pxp_commonInfo', JSON.stringify(commonInfo));
      storage.setItem('pxp_screenPageSizeInfoList', JSON.stringify(screenPageSizeInfoList.value));
      storage.setItem('pxp_authToken', token.value);
    } catch (error) {
      console.error('sessionStorageへの保存エラー:', error);
    }
  };
  // Add new method to update token
  const updateToken = (newToken: string) => {
    if (newToken && newToken !== token.value) {
      token.value = newToken;
      // トーケンを更新する
      storage.setItem('pxp_authToken', newToken);
    }
  };

  const login = async (param: any) => {
    try {
      setRequestInfo('C01F01', 'C01B02', actionName.search);
      await request.post(apiRoute('v1_authority_authenticate'), param);

      setRequestInfo('C01F01', 'C01B02', actionName.search);
      //アクセスできる機能一覧取得する
      const response: any = await request.get(apiRoute('v1_authority_getuserdetails'));
      if (response.data.length > 0) {
        userInfo.employeeId = response.data[0].employeeId;
        userInfo.employeeName = response.data[0].employeeName;
        userInfo.roleType = response.data[0].roleType;
        userInfo.accessPrctCode = param.accessPrctCode;
        userInfo.accessPrctCodeList = response.data[0].accessPrctCodeList || [];
        menuItems.value = response.data[0].functionIdAccessPrivilegeList.map((item: any) => ({
          functionId: item.functionId,
          accessPrivilege: item.accessPrivilege,
        }));
        // システム設定のページサイズ情報を保存
        screenPageSizeInfoList.value = response.data[0].screenPageSizeInfoList || [];

        // 正規表現パターンを定義
        const functionIdPattern = /^J01F01-[1-3]$/; // J01F01-1, J01F01-2, J01F01-3にマッチ
        menuItems.value.forEach((item, index) => {
          if (pageKeyToFunctionId['PACKINGPLAN'] === item.functionId) {
            // J01F01-1, J01F01-2, J01F01-3のパターンにマッチする場合
            const screenPageSize = screenPageSizeInfoList.value.filter((pageSize: any) =>
              functionIdPattern.test(pageSize.functionId),
            );
            item.subPageSizeInfo = screenPageSize ? screenPageSize : [];
          } else {
            const screenPageSize = screenPageSizeInfoList.value.find(
              (pageSize: any) => pageSize.functionId === item.functionId,
            );
            item.pageSize = screenPageSize ? screenPageSize.pageSize : 10;
          }
        });
        // 共通情報を取得
        commonInfo.maxUploadFileSize = response.data[0].maxUploadFileSize || 20_971_520;

        // ログイン成功後にsessionStorageに保存
        saveToStorage();
      }
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      token.value = '';
      Object.assign(userInfo, {
        employeeId: '',
        employeeName: '',
        prctCode: '',
        accessPrctCodeList: [],
        roleType: '',
        placeType: '',
        permissions: [],
        idToken: '',
      });
      Object.assign(commonInfo, {
        maxUploadFileSize: 0,
      });
      menuItems.value = [];
      screenPageSizeInfoList.value = [];
      // SessionStorageをクリア
      storage.clearItem();
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  // 初期化トークンはsessionStorageから取得
  const initializeToken = () => {
    const storedToken = storage.getItem('pxp_authToken');
    if (storedToken) {
      token.value = storedToken;
    }
  };

  const getUserInfo = () => {
    return userInfo;
  };

  const updateUserInfo = (inputUserInfo: Partial<UserInfo>) => {
    Object.assign(userInfo, inputUserInfo);
  };

  //該当ユーザの全ての機能とアクセス権限取得
  const getAccessPrivilegeList = () => {
    return menuItems.value;
  };
  //機能別アクセス権限取得
  const getAccessPrivilegeByFunctionId = (functionId: string) => {
    // FunctionIdからアクセス権限を取得
    if (userInfo.accessPrctCode !== userInfo.prctCode) {
      // 他社画面権限制御
      //国会社コードが100100の場合、全ての機能にアクセス権限(閲覧)を付与
      if (userInfo.prctCode == '100100') {
        return ACCESS_READ;
      } else {
        //参照可能画面
        const accessPrivilegeList = [
          'A03F01', // PXP対応カレンダーマスタ管理画面
          'B05F01', // 打ち切り品番マスタ管理画面
          'I01F01', // PXPオーダー変動チェック画面
          'I03F01', // PXP受注原数/計画数履歴画面
          'I04F01', // BOM Check List表示画面
          'J01F01-1', // 梱包計画管理画面(月次)
          'J01F01-2', // 梱包計画管理画面(週次)
          'L04F01', // 注残管理画面
        ];
        if (accessPrivilegeList.includes(functionId)) {
          return ACCESS_READ;
        } else {
          return accessPrivilegeList.find((id) => id.startsWith(functionId)) ? ACCESS_READ : null;
        }
      }
    } else {
      const menuItem = menuItems.value.find((item) => item.functionId.trim() === functionId);
      if (!menuItem) {
        return null;
      }
      // 自社画面権限制御
      return menuItem.accessPrivilege;
    }
  };

  //機能別アクセス権限取得
  const getAccessPrivilegeByPageKey = (pageKey: string) => {
    // FunctionIdを取得
    const functionId = pageKeyToFunctionId[pageKey];
    if (!functionId) {
      //console.error(`FunctionId not found for pageKey: ${pageKey}`);
      return null;
    }
    return getAccessPrivilegeByFunctionId(functionId);
  };
  //機能別ページサイズ取得
  const getPageSizeByPageKey = (pageKey: string) => {
    const functionId = pageKeyToFunctionId[pageKey];
    if (!functionId) {
      //console.error(`FunctionId not found for pageKey: ${pageKey}`);
      return 10; // デフォルト値を10に設定
    }
    // 正規表現パターンを定義
    const functionIdPattern = /^J01F01-[1-3]$/; // J01F01-1, J01F01-2, J01F01-3にマッチ
    if (functionIdPattern.test(functionId)) {
      // J01F01-1, J01F01-2, J01F01-3のパターンにマッチする場合
      const menuItem = menuItems.value.find((item) => item.functionId === 'J01F01');
      if (!menuItem || !menuItem.subPageSizeInfo) {
        // menuItemsに存在しない場合、システム設定から取得（他社ユーザ対応）
        const screenPageSize = screenPageSizeInfoList.value.find((pageSize: any) => pageSize.functionId === functionId);
        return screenPageSize ? screenPageSize.pageSize : 10;
      }
      const subPageSize = menuItem.subPageSizeInfo.find((subItem) => subItem.functionId === functionId);
      return subPageSize ? subPageSize.pageSize : 10; // デフォルト値を10に設定
    } else {
      const menuItem = menuItems.value.find((item) => item.functionId.trim() === functionId);
      if (!menuItem) {
        // menuItemsに存在しない場合、システム設定から取得（他社ユーザ対応）
        const screenPageSize = screenPageSizeInfoList.value.find((pageSize: any) => pageSize.functionId === functionId);
        return screenPageSize ? screenPageSize.pageSize : 10;
      }
      return menuItem.pageSize || 10; // デフォルト値を10に設定
    }
  };

  //アップロードファイルサイズ取得
  const getMaxUploadFileSize = () => {
    return commonInfo.maxUploadFileSize;
  };

  initializeToken();

  return {
    token,
    userInfo,
    login,
    logout,
    updateToken,
    updateUserInfo,
    getUserInfo,
    getAccessPrivilegeList,
    getAccessPrivilegeByFunctionId,
    getAccessPrivilegeByPageKey,
    restoreFromStorage,
    getPageSizeByPageKey,
    getMaxUploadFileSize,
  };
});
